import { InlineAlertType } from '@varo/graphql-schema-types';
import DebtSaleAccountSummaryCard from 'features/reactivation/components/DebtSaleSummarySection/DebtSaleAccountSummaryCard';
import {
  DebtSaleDetailsState,
  DebtSaleDetailsStatus,
  UseDebtSaleDetailsResult,
} from 'features/reactivation/hooks/useDebtSaleDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useDebtSaleAction } from 'shared/components/DebtSale/hooks/useDebtSaleAction';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import theme from 'shared/styles/theme';

export interface DebtSaleSummarySectionProps {
  result: DebtSaleDetailsState | null;
  error: UseDebtSaleDetailsResult['error'];
}

const DebtSaleSummarySection = ({
  result,
  error,
}: DebtSaleSummarySectionProps) => {
  const { handleDebtSaleAction } = useDebtSaleAction();
  const { t: translatedText } = useTranslation('debtSale', {
    keyPrefix: 'summarySection',
  });

  if (result?.status === DebtSaleDetailsStatus.Success) {
    const { pageTitle, footnote, accounts } = result.success;

    return (
      <View style={styles.container} testID="debt-sale-summary-section">
        <View style={styles.header}>
          <TitleMdText>{pageTitle}</TitleMdText>
          <BodyBaseText style={styles.footnote}>{footnote}</BodyBaseText>
        </View>
        {accounts.map((account) => {
          const handlePaymentPortalPress = () => {
            void handleDebtSaleAction(
              account.paymentPortalButton.contactMethod.action,
            );
          };

          return (
            <DebtSaleAccountSummaryCard
              key={account.id}
              title={account.productName}
              subtitle={account.subtitle}
              body={account.paymentInstructions}
              button={{
                label: account.paymentPortalButton.label,
                onPress: handlePaymentPortalPress,
              }}
            />
          );
        })}
      </View>
    );
  }

  if (error) {
    const typedError =
      result?.status === DebtSaleDetailsStatus.Error ? result.typedError : null;

    return (
      <View style={styles.container} testID="debt-sale-summary-section-error">
        <InlineAlert
          alertType={InlineAlertType.Error}
          titleText={typedError?.title || translatedText('error.title')}
          bodyText={typedError?.message || translatedText('error.message')}
        />
      </View>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
  },
  header: {
    gap: theme.spacing.s,
  },
  footnote: {
    color: theme.color.text.primary,
  },
});

export default DebtSaleSummarySection;
